import { useState, useCallback } from 'react';
import { X, MessageSquare, Video, FolderOpen, Calendar, Shield, Users, ArrowRight, ArrowLeft, Check, Sparkles, Building2, UserPlus, Rocket } from 'lucide-react';
import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores';
import { api } from '../../lib/api';
import type { View } from '../../App';

interface OnboardingWizardProps {
  onClose: () => void;
  onNavigate: (view: View) => void;
}

const STORAGE_KEY = 'eurion_onboarding_complete';

/** Check if onboarding has been completed for the current user */
export function isOnboardingComplete(userId: string): boolean {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return false;
    const parsed = JSON.parse(stored) as string[];
    return parsed.includes(userId);
  } catch {
    return false;
  }
}

/** Mark onboarding as complete for a user */
function markOnboardingComplete(userId: string) {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    const parsed: string[] = stored ? JSON.parse(stored) : [];
    if (!parsed.includes(userId)) parsed.push(userId);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
  } catch {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([userId]));
  }
}

/* ── Step components ──────────────────────────────────────────── */

function WelcomeStep() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center text-center">
      <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-nord-7/20 to-nord-10/20">
        <Sparkles className="h-10 w-10 text-nord-8" />
      </div>
      <h2 className="mb-3 text-2xl font-bold text-nord-6">{t('onboarding.welcome.title')}</h2>
      <p className="mb-6 max-w-md text-nord-4">
        {t('onboarding.welcome.intro')}
      </p>
      <div className="grid w-full max-w-sm gap-3">
        {[
          { id: 'messaging', icon: Shield, text: t('onboarding.welcome.featureMessaging') },
          { id: 'meetings', icon: Video, text: t('onboarding.welcome.featureMeetings') },
          { id: 'files', icon: FolderOpen, text: t('onboarding.welcome.featureFiles') },
          { id: 'calendar', icon: Calendar, text: t('onboarding.welcome.featureCalendar') },
        ].map(({ id, icon: Icon, text }) => (
          <div key={id} className="flex items-center gap-3 rounded-lg border border-nord-3/30 bg-nord-1/50 px-4 py-3">
            <Icon className="h-5 w-5 shrink-0 text-nord-8" />
            <span className="text-sm text-nord-5">{text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function OrgSetupStep({ orgName, setOrgName, orgDescription, setOrgDescription, saving }: {
  orgName: string;
  setOrgName: (v: string) => void;
  orgDescription: string;
  setOrgDescription: (v: string) => void;
  saving: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center text-center">
      <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-nord-14/20 to-nord-13/20">
        <Building2 className="h-10 w-10 text-nord-13" />
      </div>
      <h2 className="mb-2 text-2xl font-bold text-nord-6">{t('onboarding.org.title')}</h2>
      <p className="mb-6 max-w-md text-nord-4">
        {t('onboarding.org.intro')}
      </p>
      <div className="w-full max-w-sm space-y-4">
        <div className="text-left">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-nord-4">
            {t('onboarding.org.nameLabel')}
          </label>
          <input
            type="text"
            value={orgName}
            onChange={e => setOrgName(e.target.value)}
            placeholder={t('onboarding.org.namePlaceholder')}
            disabled={saving}
            className="w-full rounded-lg border border-nord-3/50 bg-nord-1 px-4 py-3 text-sm text-nord-6 placeholder-nord-3 focus:border-nord-8 focus:outline-none focus:ring-1 focus:ring-nord-8 disabled:opacity-50"
            autoFocus
          />
        </div>
        <div className="text-left">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-nord-4">
            {t('onboarding.org.descriptionLabel')} <span className="text-nord-3">{t('onboarding.org.optional')}</span>
          </label>
          <textarea
            value={orgDescription}
            onChange={e => setOrgDescription(e.target.value)}
            placeholder={t('onboarding.org.descriptionPlaceholder')}
            disabled={saving}
            rows={2}
            className="w-full resize-none rounded-lg border border-nord-3/50 bg-nord-1 px-4 py-3 text-sm text-nord-6 placeholder-nord-3 focus:border-nord-8 focus:outline-none focus:ring-1 focus:ring-nord-8 disabled:opacity-50"
          />
        </div>
      </div>
    </div>
  );
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** English fallback when no translator is passed (keeps the helper usable on its own). */
const englishMail: Translate = (key, options) => {
  const o = options ?? {};
  if (key === 'onboarding.invite.mailSubjectOrg') return `Join ${String(o.org)} on Eurion`;
  if (key === 'onboarding.invite.mailSubject') return 'Join me on Eurion';
  return `Hi!\n\nI've set us up on Eurion for chat, meetings and files. Sign in here:\n${String(o.url)}\n`;
};

/** mailto: link that invites everyone in one email from the user's own mail app. */
export function inviteMailto(emails: string[], appUrl: string, orgName?: string, t: Translate = englishMail): string {
  const subject = orgName
    ? t('onboarding.invite.mailSubjectOrg', { org: orgName })
    : t('onboarding.invite.mailSubject');
  const body = t('onboarding.invite.mailBody', { url: appUrl });
  return `mailto:?bcc=${emails.map(encodeURIComponent).join(',')}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

function InviteStep({ inviteEmail, setInviteEmail, invitedEmails, onInvite, orgName }: {
  inviteEmail: string;
  setInviteEmail: (v: string) => void;
  invitedEmails: string[];
  onInvite: () => void;
  orgName?: string;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center text-center">
      <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-nord-9/20 to-nord-10/20">
        <UserPlus className="h-10 w-10 text-nord-9" />
      </div>
      <h2 className="mb-2 text-2xl font-bold text-nord-6">{t('onboarding.invite.title')}</h2>
      <p className="mb-6 max-w-md text-nord-4">
        {t('onboarding.invite.intro')}
      </p>
      <div className="w-full max-w-sm space-y-4">
        <div className="flex gap-2">
          <input
            type="email"
            value={inviteEmail}
            onChange={e => setInviteEmail(e.target.value)}
            placeholder={t('onboarding.invite.emailPlaceholder')}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); onInvite(); } }}
            className="flex-1 rounded-lg border border-nord-3/50 bg-nord-1 px-4 py-3 text-sm text-nord-6 placeholder-nord-3 focus:border-nord-8 focus:outline-none focus:ring-1 focus:ring-nord-8"
          />
          <button
            onClick={onInvite}
            disabled={!inviteEmail.includes('@')}
            className="shrink-0 rounded-lg bg-nord-8 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-nord-9 disabled:opacity-40"
          >
            {t('onboarding.invite.add')}
          </button>
        </div>
        {invitedEmails.length > 0 && (
          <div className="space-y-2">
            {invitedEmails.map(email => (
              <div key={email} className="flex items-center gap-2 rounded-lg border border-nord-14/30 bg-nord-14/10 px-3 py-2 text-sm">
                <Check className="h-4 w-4 text-nord-14" />
                <span className="text-nord-5">{email}</span>

              </div>
            ))}
            {/* Eurion doesn't send invitation emails yet: hand the list to the user's mail app */}
            <a
              href={inviteMailto(invitedEmails, window.location.origin, orgName, t)}
              className="block rounded-lg bg-nord-8 px-4 py-2.5 text-sm font-medium text-white hover:bg-nord-9"
            >
              {t('onboarding.invite.sendFromEmail', { n: invitedEmails.length })}
            </a>
          </div>
        )}
        <p className="text-xs text-nord-3">{t('onboarding.invite.skipHint')}</p>
      </div>
    </div>
  );
}

function QuickStartStep({ onNavigate, onClose }: { onNavigate: (view: View) => void; onClose: () => void }) {
  const { t } = useTranslation();
  const actions: { view: View; icon: typeof MessageSquare; label: string; description: string; color: string }[] = [
    { view: 'chat', icon: MessageSquare, label: t('onboarding.quickStart.chatLabel'), description: t('onboarding.quickStart.chatDescription'), color: 'text-nord-8 bg-nord-8/10' },
    { view: 'teams', icon: Users, label: t('onboarding.quickStart.teamLabel'), description: t('onboarding.quickStart.teamDescription'), color: 'text-nord-14 bg-nord-14/10' },
    { view: 'meet', icon: Video, label: t('onboarding.quickStart.meetLabel'), description: t('onboarding.quickStart.meetDescription'), color: 'text-nord-13 bg-nord-13/10' },
    { view: 'calendar', icon: Calendar, label: t('onboarding.quickStart.calendarLabel'), description: t('onboarding.quickStart.calendarDescription'), color: 'text-nord-15 bg-nord-15/10' },
  ];

  return (
    <div className="flex flex-col items-center text-center">
      <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-nord-14/20 to-nord-8/20">
        <Rocket className="h-10 w-10 text-nord-14" />
      </div>
      <h2 className="mb-2 text-2xl font-bold text-nord-6">{t('onboarding.quickStart.title')}</h2>
      <p className="mb-6 max-w-md text-nord-4">
        {t('onboarding.quickStart.intro')}
      </p>
      <div className="w-full max-w-sm space-y-3">
        {actions.map(({ view, icon: Icon, label, description, color }) => (
          <button
            key={view}
            onClick={() => { onClose(); onNavigate(view); }}
            className="flex w-full items-center gap-4 rounded-xl border border-nord-3/30 bg-nord-1/50 px-4 py-3 text-left transition-all hover:border-nord-8/40 hover:bg-nord-1"
          >
            <div className={clsx('flex h-10 w-10 shrink-0 items-center justify-center rounded-lg', color)}>
              <Icon className="h-5 w-5" />
            </div>
            <div>
              <div className="text-sm font-semibold text-nord-6">{label}</div>
              <div className="text-xs text-nord-4">{description}</div>
            </div>
            <ArrowRight className="ml-auto h-4 w-4 text-nord-3" />
          </button>
        ))}
      </div>
    </div>
  );
}

/* ── Main Wizard ──────────────────────────────────────────────── */

const TOTAL_STEPS = 4;

export function OnboardingWizard({ onClose, onNavigate }: OnboardingWizardProps) {
  const { t } = useTranslation();
  const user = useAuthStore(s => s.user);
  const [step, setStep] = useState(0);
  const [orgName, setOrgName] = useState('');
  const [orgDescription, setOrgDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [invitedEmails, setInvitedEmails] = useState<string[]>([]);

  const handleClose = useCallback(() => {
    if (user?.id) markOnboardingComplete(user.id);
    onClose();
  }, [user?.id, onClose]);

  const handleNext = useCallback(async () => {
    // On step 1 (org setup), save org name if provided (org-service lets only admins rename the org)
    const canEditOrg = user?.roles?.some(r => ['org_admin', 'admin', 'super_admin', 'super-admin'].includes(r));
    if (step === 1 && orgName.trim() && user?.orgId && canEditOrg) {
      setSaving(true);
      try {
        await api.patch(`/v1/orgs/${user.orgId}`, {
          name: orgName.trim(),
          ...(orgDescription.trim() ? { description: orgDescription.trim() } : {}),
        });
      } catch {
        // Non-blocking — don't prevent advancing
      }
      setSaving(false);
    }
    if (step < TOTAL_STEPS - 1) setStep(s => s + 1);
  }, [step, orgName, orgDescription, user?.orgId]);

  const handleBack = () => { if (step > 0) setStep(s => s - 1); };

  const handleInvite = useCallback(() => {
    const email = inviteEmail.trim().toLowerCase();
    if (!email.includes('@') || invitedEmails.includes(email)) return;
    setInvitedEmails(prev => [...prev, email]);
    setInviteEmail('');
  }, [inviteEmail, invitedEmails]);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={handleClose}>
      <div
        className="relative mx-4 w-full max-w-lg rounded-2xl border border-nord-3/40 bg-nord-0 shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute right-4 top-4 rounded-lg p-1.5 text-nord-4 transition-colors hover:bg-nord-2 hover:text-nord-6"
          aria-label={t('common.close')}
        >
          <X className="h-5 w-5" />
        </button>

        {/* Progress dots */}
        <div className="flex items-center justify-center gap-2 pt-6">
          {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
            <div
              key={i}
              className={clsx(
                'h-2 rounded-full transition-all duration-300',
                i === step ? 'w-8 bg-nord-8' : i < step ? 'w-2 bg-nord-8/50' : 'w-2 bg-nord-3/40',
              )}
            />
          ))}
        </div>

        {/* Step content */}
        <div className="px-6 py-8 sm:px-8">
          {step === 0 && <WelcomeStep />}
          {step === 1 && <OrgSetupStep orgName={orgName} setOrgName={setOrgName} orgDescription={orgDescription} setOrgDescription={setOrgDescription} saving={saving} />}
          {step === 2 && <InviteStep inviteEmail={inviteEmail} setInviteEmail={setInviteEmail} invitedEmails={invitedEmails} onInvite={handleInvite} orgName={orgName} />}
          {step === 3 && <QuickStartStep onNavigate={onNavigate} onClose={handleClose} />}
        </div>

        {/* Navigation buttons */}
        <div className="flex items-center justify-between border-t border-nord-3/30 px-6 py-4 sm:px-8">
          <button
            onClick={handleBack}
            disabled={step === 0}
            className="flex items-center gap-1 rounded-lg px-4 py-2 text-sm font-medium text-nord-4 transition-colors hover:text-nord-6 disabled:invisible"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('common.back')}
          </button>

          <button onClick={handleClose} className="text-sm text-nord-3 hover:text-nord-4">
            {t('onboarding.skip')}
          </button>

          {step < TOTAL_STEPS - 1 ? (
            <button
              onClick={handleNext}
              disabled={saving}
              className="flex items-center gap-1 rounded-lg bg-nord-8 px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-nord-9 disabled:opacity-50"
            >
              {saving ? t('onboarding.saving') : t('onboarding.next')}
              {!saving && <ArrowRight className="h-4 w-4" />}
            </button>
          ) : (
            <button
              onClick={handleClose}
              className="flex items-center gap-1 rounded-lg bg-nord-14 px-5 py-2 text-sm font-medium text-nord-0 transition-colors hover:bg-nord-14/80"
            >
              {t('onboarding.getStarted')}
              <Check className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
